import Core                  from '@/core/Core';
import Characteristic        from '@/domains/Characteristics/Characteristic';
import CharacteristicsDAO    from '@/domains/Characteristics/CharacteristicsDAO';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import CharacteristicGroup from "@/domains/CharacteristicGroups/CharacteristicGroup";

/**
 * Сервис для работы с рамками характеристик
 */
class CharacteristicsService {

	// ============================================================
	// CRUD
	// ============================================================

	/**
	 * Загрузить одну характеристику по id (включая удалённые)
	 */
	static async get(id: number): Promise<Characteristic | null> {
		const result = (await this.getListPaginated({ id })).data;
		return result[0] ?? null;
	}

	/**
	 * Загрузить список характеристик с пагинацией.
	 * Используется в реестре через DataGridLoader, а также везде, где нужен список
	 * (диалоги, автокомплиты) — пагинацию можно просто не передавать.
	 */
	static async getListPaginated(
		params?: Parameters<typeof CharacteristicsDAO.getList>[0] & {
			pagination?: RpcDataPagination;
			sort?: RpcDataSort;
		}
	): Promise<{ data: Characteristic[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await CharacteristicsDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, Characteristic.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Получить рамки характеристик, привязанные к категории и/или продукту.
	 * Используется потребителями-сущностями (Offer, Product) — возвращает уже
	 * гидрированный массив без пагинации.
	 */
	static async getAttachedList(params?: {
		categoryId?: number | number[];
		productId?: number | number[];
	}): Promise<Characteristic[]> {
		const response = await CharacteristicsDAO.getAttachedList(params);
		return Core.utils.hydrateArray(response.data, Characteristic.fromServer);
	}

	/**
	 * Создать характеристику
	 */
	static async add(characteristic: Characteristic): Promise<boolean> {
		const response = await CharacteristicsDAO.add(characteristic);

		if (response.ok) {
			Core.showNotification.success('Характеристика успешно добавлена');
		}

		return response.ok;
	}

	/**
	 * Обновить характеристику
	 */
	static async update(characteristic: Characteristic): Promise<boolean> {
		const response = await CharacteristicsDAO.update(characteristic);

		if (response.ok) {
			Core.showNotification.success('Характеристика обновлена');
		}

		return response.ok;
	}

	/**
	 * Установить группу для характеристики
	 */
	static async setGroup(characteristicId: number, groupId: number): Promise<boolean> {
		const response = await CharacteristicsDAO.setGroup(characteristicId, groupId);

		if (response.ok) {
			Core.showNotification.success('Группа характеристики обновлена');
		}

		return response.ok;
	}

	/**
	 * Удалить характеристику
	 */
	static async delete(id: number): Promise<boolean> {
		const response = await CharacteristicsDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Характеристика удалена');
		}

		return response.ok;
	}

	/**
	 * Удалить характеристику с подтверждением через диалог.
	 * Используется в местах, где нужен UX (карточка, контекстное меню).
	 */
	static async deleteWithConfirmation(characteristic: Characteristic): Promise<boolean> {
		const confirmed = await Core.dialogs.confirm(
			'Удалить эту характеристику?',
			`Характеристика: "${characteristic.name}"`,
		);

		if (confirmed === true) {
			return await this.delete(characteristic.id);
		} else {
			return false;
		}
	}

	/**
	 * Обновить обязательность характеристики для категории продукта
	 */
	static async isRequiredUpdate(
		characteristicId: number,
		productCategoryId: number,
		isRequired: boolean,
	): Promise<boolean> {
		const response = await CharacteristicsDAO.isRequiredUpdate(
			characteristicId,
			productCategoryId,
			isRequired,
		);

		if (response.ok) {
			Core.showNotification.success('Обязательность характеристики обновлена');
		}

		return response.ok;
	}

	/**
	 * Изменить порядок сортировки характеристик
	 */
	static async changeSort(
		movedElementId: number,
		targetElementId: number,
		insertAfter: boolean,
	): Promise<boolean> {
		const response = await CharacteristicsDAO.changeSort(movedElementId, targetElementId, insertAfter);

		if (response.ok) {
			Core.showNotification.success('Порядок характеристик обновлён');
		}

		return response.ok;
	}

	/**
	 * Показывает карточку характеристики.
	 * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
	 */
	static showCharacteristicCard(
		characteristicOrId?: Characteristic | number,
		group?: CharacteristicGroup,
	): Promise<boolean> {
		return Core.dialogs.CharacteristicCard.showAsPromise({
			characteristicOrId,
			group,
		});
	}

	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(characteristic: Characteristic) => { return this.showCharacteristicCard(characteristic); },
				null,
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(characteristic: Characteristic) => { return this.deleteWithConfirmation(characteristic); },
			),
		];
	}

}

export default CharacteristicsService;